import type { CreateResult, DataProvider } from "ra-core";

/**
 * Create a contact from an import, stamped `imported_at` so the database
 * reports its contact.created as `via: "import"` and "Contact is created"
 * workflows skip it (decision #102) - importing a list must never email the
 * whole list. Every import path goes through here.
 *
 * Tenant migrations are applied by hand after the frontend deploys, so a
 * workspace may not have the column yet: that create fails naming the
 * column, and it's retried unmarked rather than breaking the import.
 *
 * Returns ra-core's default CreateResult - the same type a plain
 * dataProvider.create("contacts", ...) gives - so call sites type as before.
 */
export async function createImportedContact(
  dataProvider: DataProvider,
  data: Record<string, unknown>,
): Promise<CreateResult> {
  try {
    return await dataProvider.create("contacts", {
      data: { ...data, imported_at: new Date().toISOString() },
    });
  } catch (e) {
    if (!mentionsImportedAt(e)) throw e;
    return dataProvider.create("contacts", { data });
  }
}

const mentionsImportedAt = (e: unknown) => {
  const body = (e as { body?: unknown } | null)?.body;
  const text = `${e instanceof Error ? e.message : String(e)} ${body ? JSON.stringify(body) : ""}`;
  return text.includes("imported_at");
};
